import { type SearchImagesQuery, useSearchImagesInfiniteQuery } from '@vizit/api';
import { useShowTagging } from '@vizit/feature-flags';
import { useInfiniteScroll } from '@vizit/hooks';
import { calculateOptimalLoadCount, debounce } from '@vizit/utils';
import * as React from 'react';
import { transformStateToQueryParams, useAssetTable } from '../../providers';

/**
 * First page size that fills the viewport. Recomputed on window resize, and when the grid switches between responsive
 * and fixed columns (the feature flag can resolve after the first render, so `columns` arrives late on a cold load).
 * Not on every `columns` change: the count is the first page's `limit`, so that would refetch the whole grid while the
 * user drags the size slider. Later pages still come from infinite scroll.
 */
function useOptimalInitialLoadCount(columns?: number): number {
  const columnsRef = React.useRef(columns);
  columnsRef.current = columns;

  const hasFixedColumns = columns !== undefined;
  // Initialize with calculated value to avoid extra render
  const [loadCount, setLoadCount] = React.useState(() => ({
    count: calculateOptimalLoadCount(columns),
    hasFixedColumns,
  }));

  // Switched between responsive and fixed columns: recompute during render, not in an effect, so the grid never
  // fetches a page sized for the old columns first
  let { count } = loadCount;
  if (loadCount.hasFixedColumns !== hasFixedColumns) {
    count = calculateOptimalLoadCount(columns);
    setLoadCount({ count, hasFixedColumns });
  }

  React.useEffect(() => {
    // Debounced resize handler to limit calculations during window resizing
    const debouncedHandleResize = debounce(() => {
      const current = columnsRef.current;
      const next = { count: calculateOptimalLoadCount(current), hasFixedColumns: current !== undefined };
      // Same count, same object: no re-render on every resize step
      setLoadCount((prev) =>
        prev.count === next.count && prev.hasFixedColumns === next.hasFixedColumns ? prev : next,
      );
    }, 250); // 250ms debounce

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  return count;
}

interface UseAssetGridViewerProps {
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Fixed images per row the grid renders, from the thumbnail size slider. Pages are then sized to fill the screen,
   * like in the Collection grid. `undefined` keeps the responsive columns and the default page size
   */
  columns?: number;
}

/**
 * Hook to fetch portfolio assets for the asset grid viewer with infinite loading
 * Uses the generated getImages query internally and automatically calculates optimal initial load count
 */
function useAssetGridViewer(props: UseAssetGridViewerProps) {
  const { useOptimalInitialLoad = true, columns } = props;

  const {
    workspaceId,
    limit: tableLimit,
    sortBy,
    sortDirection,
    productCategoryId,
    imageType,
    tagIds: selectedTagIds,
  } = useAssetTable();
  const showTagging = useShowTagging();
  const tagIds = showTagging ? selectedTagIds : undefined;

  const optimalInitialCount = useOptimalInitialLoadCount(columns);

  // Use optimal count for initial load, standard limit for subsequent pages
  const effectiveInitialLimit = useOptimalInitialLoad ? optimalInitialCount : tableLimit;
  // With 10 images per row the default 12 is barely one row, so with fixed columns every page fills the screen
  const limit = columns ? effectiveInitialLimit : tableLimit;

  const filterQueryParams = React.useMemo(
    () =>
      transformStateToQueryParams({
        limit,
        sortBy,
        sortDirection,
        productCategoryId,
        imageType,
        tagIds,
      }),
    [limit, sortBy, sortDirection, productCategoryId, imageType, tagIds],
  );

  const { data, isLoading, error, hasNextPage, hasPreviousPage, isFetchingNextPage, fetchNextPage } =
    useSearchImagesInfiniteQuery({
      workspace_id: workspaceId ?? '',
      source: undefined,
      exclude_source: ['generated'],
      parent_id: undefined,
      ...filterQueryParams,
    });

  const assets = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchImagesQuery[] } | undefined;
    const allImages = infiniteData?.pages?.flatMap((page) => page.data) || [];
    return allImages;
  }, [data]);

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchImagesQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  const sentinelRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });

  // Use hasPreviousPage to determine loading count
  // hasPreviousPage = false: initial load (show optimal count)
  // hasPreviousPage = true: subsequent loads (show standard limit)
  const loadingCount = React.useMemo(() => {
    if (isLoading && !hasPreviousPage) {
      // Initial load: show optimal initial count skeletons (e.g., 24)
      return effectiveInitialLimit;
    }
    if (isFetchingNextPage) {
      // Subsequent loads: show standard limit skeletons (e.g., 12)
      return limit;
    }
    // Not loading: no skeletons needed
    return 0;
  }, [isLoading, hasPreviousPage, isFetchingNextPage, effectiveInitialLimit, limit]);

  return {
    workspaceId,
    assets,
    totalCount,
    isLoading,
    isTagFiltered: !!tagIds?.length,
    hasError: !!error,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
  };
}

export { useAssetGridViewer };
export type { UseAssetGridViewerProps };
